(ns frontend.ux-overhaul.listing.generic.bulk-actions
  "Shared, entity-agnostic implementations of the selection-dependent
  toolbar actions every legacy grid had (bulk-delete, bulk-edit, export) -
  extracted from the kyc/screening/pipeline-entity listings' own
  near-identical copies of this logic so the remaining ported entities don't
  each need their own ~100-line reimplementation. A listing's own
  event.cljs should stay a thin per-entity adapter: supply the entity type,
  the backend mass-action operation + request body shape (these differ
  slightly per entity - see ai/mistakes.md), and dispatch into these
  shared events/helpers."
  (:require
   [common.localization :as loc]
   [common.schema.listing-definition :as listing-definition]
   [frontend.conn :as conn]
   [frontend.logging :as logging]
   [frontend.utils :as fu]
   [frontend.ux-overhaul.listing.generic.event :as generic-event]
   [frontend.ux-overhaul.listing.generic.model :as generic-listing-model]
   [frontend.ux-overhaul.listing.generic.selection :as selection-helpers]
   [frontend.ux-overhaul.listing.generic.sub :as generic-sub]
   [frontend.view.modals.bulk-edit-entity.view :as bulk-edit-entity-modal]
   [frontend.view.modals.confirmation.view :as confirmation]
   [frontend.view.modals.events :as modals]
   [fx.ajax :as ajax]
   [promesa.core :as p]
   [re-frame.core :as rf]
   [reagent.core :as r]
   [ui.completion.event :as completion-event]
   [ui.fx.toast :as toast]
   [ui.view.grid-opensearch.event :as grid-event]
   [ui.editors.simple-filters.model :as simple-filters-model]
   [ui.view.grid-opensearch.model :as grid-model]
   [ui.view.kanban-opensearch.model :as kanban-model]))

;; -----------------------------------------------------------------------
;; Bulk edit modal
;; -----------------------------------------------------------------------

(defn- bulk-edit-open-path
  [listing-id]
  (conj (generic-listing-model/listing-path listing-id) :bulk-edit-open?))

(rf/reg-sub
 ::bulk-edit-open?
 (fn [db [_ listing-id]]
   (get-in db (bulk-edit-open-path listing-id) false)))

(rf/reg-event-db
 ::open-bulk-edit
 (fn [db [_ {:keys [listing-id]}]]
   (assoc-in db (bulk-edit-open-path listing-id) true)))

(rf/reg-event-fx
 ::close-bulk-edit
 (fn [{:keys [db]} [_ {:keys [listing-id]}]]
   {:db (assoc-in db (bulk-edit-open-path listing-id) false)
    :fx [[:dispatch [::grid-event/trigger-query-search (str listing-id "-grid")]]]}))

(defn bulk-edit-modal
  "Drop into a listing's :reagent-render alongside [listing/view ...] to
  wire up the shared frontend.view.modals.bulk-edit-entity.view against the
  current selection - fabricates the fake grid-state atom that component
  expects purely from the generic engine's own selected-ids/context, no
  per-entity setup needed."
  [{:keys [listing-id entity-type on-save-success]}]
  (let [open? @(rf/subscribe [::bulk-edit-open? listing-id])]
    (when open?
      (let [selected-ids @(rf/subscribe [::generic-sub/selected-ids listing-id])
            context @(rf/subscribe [::generic-sub/context listing-id])
            accessible-field-paths (->> (or (:accessible-fields context) [])
                                        (keep :field-path)
                                        vec)
            setup-state (r/atom {:selected             (->> selected-ids
                                                            (map (fn [id] [id true]))
                                                            (into {}))
                                 :sorted-grid-entities (->> selected-ids
                                                            (mapv (fn [id] {:db/id id})))})
            setup-args {:state       setup-state
                        :column-keys (fn [] accessible-field-paths)
                        :mandatory?  (fn [_] false)}]
        [bulk-edit-entity-modal/view
         {:setup-args      setup-args
          :entity-type     entity-type
          :on-save-success (or on-save-success (fn []))
          :close-modal!    #(rf/dispatch [::close-bulk-edit {:listing-id listing-id}])}]))))

;; -----------------------------------------------------------------------
;; Bulk delete
;; -----------------------------------------------------------------------

(rf/reg-event-fx
 ::max-count-exceeded
 (fn [_ [_ message]]
   {::toast/error message}))

(defn bulk-delete-selected!
  "Confirms, then dispatches `operation` (a :<entity>/mass-action-shaped
  sendp! op) with (body-fn selected-ids) as its request body, then clears
  selection and refreshes the grid on success. `db` is the calling
  reg-event-fx handler's own {:keys [db]} - this fn only reads it, never
  returns a new one, so call it for side effects and return {} yourself.

  `max-count`, when given, blocks the action with a toast instead of
  opening the confirmation modal once the selection exceeds it - for
  entities where legacy capped bulk-delete below the usual generous
  default (e.g. fund's cascade risk to its commitments/holdings)."
  [{:keys [db listing-id operation body-fn max-count]}]
  (let [selection (get-in db (generic-listing-model/selection-path listing-id))
        selected-ids (selection-helpers/action-ids selection)]
    (cond
      (empty? selected-ids)
      nil

      (and max-count (> (count selected-ids) max-count))
      (rf/dispatch [::max-count-exceeded
                    (loc/tr :bulk-delete-max-count-exceeded [max-count])])

      :else
      (modals/open-modal!
       confirmation/modal-key
       {:body          (selection-helpers/delete-confirmation-body selection)
        :confirm-text  (loc/tr :confirm-delete-selected-button)
        :checkbox-text (loc/tr :confirm-delete-selected-checkbox)
        :on-confirm    (fn []
                         (-> (conn/sendp! operation (body-fn selected-ids))
                             (p/then (fn [resp]
                                       (rf/dispatch [::generic-event/clear-selection listing-id])
                                       ;; The delete's search-document rewrite runs off
                                       ;; the request thread, so refetching the instant
                                       ;; this reply lands can read the pre-delete
                                       ;; documents straight back and show the rows the
                                       ;; user just deleted. Wait for the indexing to
                                       ;; report done - with ui.completion's deadline, so
                                       ;; a lost message costs a too-early refetch rather
                                       ;; than a stuck listing.
                                       (rf/dispatch
                                        [::completion-event/await
                                         {:completion-id (:completion/id resp)
                                          :on-complete   [::grid-event/trigger-query-search
                                                          (str listing-id "-grid")]}])))
                             (p/catch (fn [err]
                                        (logging/error (str "Unable to delete selected " (name listing-id)) err)))))}))
    nil))

;; -----------------------------------------------------------------------
;; Export
;; -----------------------------------------------------------------------

(defn- normalize-field-path
  [field-path]
  (cond
    (vector? field-path) field-path
    (keyword? field-path) [field-path]
    (sequential? field-path) (vec field-path)
    :else nil))

(rf/reg-event-fx
 ::export-grid-started
 (fn [_ _]
   {::toast/info {:label (loc/tr :export-preparing)}}))

(rf/reg-event-fx
 ::export-grid-succeeded
 (fn [_ [_ {:keys [grid-id]} resp]]
   ;; The endpoint omits :attachment-id and reports :row-count 0 when nothing
   ;; matched, rather than uploading an empty spreadsheet.
   (let [attachment-id (get-in resp [:body :attachment-id])]
     (if attachment-id
       (do
         (fu/open-attachment! attachment-id)
         {:fx [[:dispatch [::grid-event/trigger-query-search grid-id]]]
          ::toast/success (loc/tr :export-ready-download-started)})
       {::toast/info {:label (loc/tr :export-nothing-to-export)}}))))

(defn- export-error-message
  [err]
  (let [friendly-message (get-in err [:client-friendly-error-data :message])
        fallback-message (:message err)]
    (cond
      (and (string? friendly-message) (seq friendly-message)) friendly-message
      (and (string? fallback-message) (seq fallback-message)) fallback-message
      :else (loc/tr :export-generic-retry))))

(rf/reg-event-fx
 ::export-grid-failed
 (fn [_ [_ err]]
   (let [error-body (or (:response err) err)
         error-message (export-error-message error-body)]
     (logging/error "Unable to export grid" err)
     {::toast/error (loc/tr :export-failed [error-message])})))

(rf/reg-event-fx
 ::run-export-grid
 (fn [{:keys [db]} [_ {:keys [listing-id entity-type include-associated-persons?]}]]
   (let [runtime (get-in db (generic-listing-model/listing-path listing-id))
         view-mode (get-in runtime [:view :mode])
         grid-id (str listing-id "-grid")
         kanban-id (str listing-id "-kanban")
         grid-state (get-in db (grid-model/grid-path grid-id))
         kanban-compiled-fields (->> (get-in db (conj (kanban-model/compiled-config-path kanban-id) :fields) [])
                                     (keep normalize-field-path)
                                     vec)
         filter-model (get grid-state grid-model/filters-key)
         config (get grid-state grid-model/config-key)
         ;; The toolbar's query text and filters belong to the LISTING, not to
         ;; one view mode - see the matching note in
         ;; frontend.ux-overhaul.listing.pipeline-entity. Reading the grid's
         ;; copy meant a kanban export ignored them entirely, because the grid
         ;; component is never mounted in kanban view. `filter-model` above
         ;; stays grid-scoped: it is the ag-grid column filter.
         filters-editor-id (or (get-in runtime [:filters :editor-id])
                               (str listing-id "-filters"))
         external-filters-by-field-path (get-in db (conj (simple-filters-model/filters-path filters-editor-id)
                                                         :by-field-path))
         external-filter-context (get-in db (generic-listing-model/context-path listing-id))
         external-query-text (get-in db (conj (generic-listing-model/query-path listing-id) :text))
         {:keys [columns hidden-columns]} config
         grid-visible-columns (->> columns
                                   (remove (set hidden-columns))
                                   vec)
         card-selected-fields (->> (get-in runtime [:view-config :card :by-view-mode view-mode :body-fields])
                                   (keep normalize-field-path)
                                   vec)
         default-card-fields (->> (or (:body-fields (listing-definition/card-config entity-type view-mode)) [])
                                  (keep normalize-field-path)
                                  vec)
         effective-card-fields (if (seq card-selected-fields)
                                 card-selected-fields
                                 default-card-fields)
         export-fields (if (= view-mode :kanban)
                         (cond
                           (seq kanban-compiled-fields) kanban-compiled-fields
                           (seq effective-card-fields) effective-card-fields
                           ;; Last resort, so a kanban export cannot die with
                           ;; "No columns selected for export" when neither the
                           ;; compiled kanban config nor any card body-fields
                           ;; resolve - which is exactly how the KYC listing's
                           ;; export was found dead in its own default view
                           ;; mode (see ai/mistakes.md). Not a hardcoded
                           ;; per-type list: this helper serves ~18 listings,
                           ;; and grid-visible-columns is not usable here
                           ;; because the grid component is never mounted in
                           ;; kanban view, so `columns` is nil. The listing
                           ;; definition is static config and always available,
                           ;; and po-default-field-paths already normalizes and
                           ;; falls back to :list/default-columns - i.e. export
                           ;; what the grid would show by default for this type.
                           :else (listing-definition/po-default-field-paths entity-type))
                         grid-visible-columns)
         query-context (assoc (or external-filter-context {})
                              :entity-type entity-type
                              :query-field-paths export-fields)
         request-body {:entity-type entity-type
                       :fields export-fields
                       :opensearch-queries (concat []
                                                   (listing-definition/toolbar-filter-clauses external-filters-by-field-path
                                                                                              external-filter-context)
                                                   (listing-definition/toolbar-query-clauses external-query-text
                                                                                             query-context)
                                                   (when filter-model
                                                     (grid-event/filter-model->os-clauses filter-model columns)))
                       :pipeline-id (get-in runtime [:params :pipeline-id])
                       :include-associated-persons? (boolean include-associated-persons?)}]
     (if (seq export-fields)
       {:fx [[:dispatch [::export-grid-started]]
             [::ajax/send {:operation :search/export-grid
                           :body request-body
                           :on-ok [::export-grid-succeeded {:listing-id listing-id :grid-id grid-id}]
                           :on-error [::export-grid-failed]}]]}
       {:fx [[:dispatch [::export-grid-failed {:message (loc/tr :export-no-columns-selected)}]]]}))))

(defn export-grid!
  "Dispatch a whole-listing (filtered/sorted, not selection-scoped) Excel
  export - matches the legacy grids' export-button/export-with-persons-button
  behavior. Call from a listing's own ::export-grid /
  ::export-grid-with-persons handler."
  [{:keys [listing-id entity-type include-associated-persons?]}]
  (rf/dispatch [::run-export-grid {:listing-id                   listing-id
                                   :entity-type                  entity-type
                                   :include-associated-persons? include-associated-persons?}]))
